import type { GqlTagType } from "../rewardsIndexer";
import { DEFAULT_LIMIT, gqlHelper, GraphQLClientType, MAX_PAGES } from "../rewardsIndexer/gqlHelper";

/** A single USDT `Transfer` event indexed by Envio, normalized to JS-friendly types. */
export type UsdtDepositEvent = {
  id: string;
  from: string;
  to: string;
  /** Amount in micro-USDT (USDT has 6 decimals on Arbitrum). */
  value: bigint;
  timestamp: number;
  txHash: string;
};

type RawUsdtDeposit = {
  id: string;
  from: string;
  to: string;
  value: string;
  timestamp: string;
  txHash: string;
};

type QueryVars = Record<string, unknown> & {
  toAddress: string;
  fromTimestamp: number;
  limit: number;
  after: number;
};

type QueryResp = {
  USDT_Transfer: RawUsdtDeposit[];
};

/**
 * Fetches USDT `Transfer` events to `toAddress` with `timestamp > fromTimestamp`.
 * Paginates internally via {@link gqlHelper}.
 */
export async function getDepositsToAddress(
  toAddress: string,
  fromTimestamp: number,
  GQL_URL: string,
  gql: GqlTagType,
  GraphQLClientClass: new (url: string) => GraphQLClientType,
  limit: number = DEFAULT_LIMIT,
  maxPages: number = MAX_PAGES,
): Promise<UsdtDepositEvent[]> {
  const DEPOSITS_QUERY = gql`
    query depositsToAddress($toAddress: String!, $fromTimestamp: numeric!, $limit: Int!, $after: Int) {
      USDT_Transfer(
        where: { to: { _eq: $toAddress }, timestamp: { _gt: $fromTimestamp } }
        limit: $limit
        offset: $after
        order_by: { timestamp: asc }
      ) {
        id
        from
        to
        value
        timestamp
        txHash
      }
    }
  `;

  const normalizedAddress = toAddress.toLowerCase();
  const raw = await gqlHelper<RawUsdtDeposit, QueryVars, QueryResp>(
    GQL_URL,
    DEPOSITS_QUERY,
    (offset, pageLimit) => ({ toAddress: normalizedAddress, fromTimestamp, limit: pageLimit, after: offset }),
    (res) => res.USDT_Transfer,
    GraphQLClientClass,
    limit,
    maxPages,
  );

  return raw.map((r) => ({
    id: r.id,
    from: r.from.toLowerCase(),
    to: r.to.toLowerCase(),
    value: BigInt(r.value),
    timestamp: Number(r.timestamp),
    txHash: r.txHash,
  }));
}
